import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import papi, { formatErr } from "@/lib/partnerApi";
import PartnerNav from "@/pages/partner/PartnerNav";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Loader2, Download, CheckCheck, Users, FileText } from "lucide-react";

const TABS = [["all", "All"], ["pending", "Pending"], ["approved", "Approved"], ["live", "Live"], ["hold", "Hold"], ["paused", "Paused"], ["rejected", "Rejected"], ["removed", "Removed"]];
const ACTIONS = [["approved", "Approve", "bg-emerald-600"], ["live", "Make Live", "bg-[#0E9AC8]"], ["hold", "Hold", "bg-blue-600"], ["paused", "Pause", "bg-slate-500"], ["rejected", "Reject", "bg-red-600"]];
const SS = { pending: "bg-amber-100 text-amber-700", approved: "bg-emerald-100 text-emerald-700", live: "bg-teal-100 text-teal-700", paused: "bg-slate-100 text-slate-600", hold: "bg-blue-100 text-blue-700", rejected: "bg-red-100 text-red-700", suspended: "bg-red-100 text-red-700", removed: "bg-slate-200 text-slate-500" };

export default function ManagerPortal() {
  const navigate = useNavigate();
  const [tab, setTab] = useState("pending");
  const [vendors, setVendors] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [sel, setSel] = useState([]);
  const [detail, setDetail] = useState(null);
  const [note, setNote] = useState("");
  const [loading, setLoading] = useState(true);
  const role = localStorage.getItem("partner_role");

  const load = async () => {
    setLoading(true);
    try {
      const [v, a] = await Promise.all([papi.get(`/partner/manage/vendors?status=${tab}`), papi.get("/partner/manage/analytics")]);
      setVendors(v.data); setAnalytics(a.data); setSel([]);
    } catch (e) { if ([401, 403].includes(e.response?.status)) navigate("/login"); }
    finally { setLoading(false); }
  };
  useEffect(() => { if (!["manager", "admin"].includes(role)) { navigate("/login"); return; } load(); }, [tab]);

  const openDetail = async (id) => { const { data } = await papi.get(`/partner/manage/vendors/${id}`); setDetail(data); setNote(""); };
  const setStatus = async (id, status) => {
    await papi.put(`/partner/manage/vendors/${id}/status`, { status });
    toast.success(`Marked ${status}`);
    if (detail) openDetail(id);
    load();
  };
  const bulkApprove = async () => {
    if (!sel.length) return toast.error("Select vendors first");
    await papi.post("/partner/manage/vendors/bulk-status", { ids: sel, status: "approved" });
    toast.success(`Approved ${sel.length} vendor(s)`); load();
  };
  const addNote = async () => { if (!note.trim()) return; await papi.post(`/partner/manage/vendors/${detail.id}/notes`, { note }); setNote(""); openDetail(detail.id); toast.success("Note added"); };
  const toggleVerify = async (id, verified) => {
    await papi.put(`/partner/manage/vendors/${id}/verify`, { verified });
    toast.success(verified ? "Verified as Nirmantra Partner" : "Verification removed");
    if (detail) openDetail(id); load();
  };
  const removeVendor = async (id) => {
    if (!window.confirm("Remove this vendor from the network? They will no longer be public.")) return;
    await papi.delete(`/partner/manage/vendors/${id}`);
    toast.success("Vendor removed"); setDetail(null); load();
  };
  const exportCsv = async () => {
    const res = await papi.get("/partner/manage/export", { responseType: "blob" });
    const url = URL.createObjectURL(res.data); const a = document.createElement("a"); a.href = url; a.download = "vendors.csv"; a.click();
  };

  return (
    <div className="min-h-screen bg-[#FBFAF9]">
      <PartnerNav />

      <main className="max-w-7xl mx-auto px-6 py-8">
        {analytics && (
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3 mb-6">
            <div className="card-premium rounded-xl p-4"><p className="font-display text-2xl font-extrabold text-slate-900">{analytics.total}</p><p className="text-xs text-slate-500">Total</p></div>
            {TABS.slice(1).map(([k, l]) => (
              <div key={k} className="card-premium rounded-xl p-4"><p className="font-display text-2xl font-extrabold text-[#0E9AC8]">{analytics.by_status[k] || 0}</p><p className="text-xs text-slate-500">{l}</p></div>
            ))}
          </div>
        )}

        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <div className="flex flex-wrap gap-1.5">
            {TABS.map(([k, l]) => (
              <button key={k} data-testid={`mtab-${k}`} onClick={() => setTab(k)} className={`rounded-lg px-3 py-1.5 text-sm font-medium transition-colors ${tab === k ? "bg-[#0E9AC8] text-white" : "bg-white border border-slate-200 text-slate-600 hover:border-[#0E9AC8]"}`}>{l}</button>
            ))}
          </div>
          <div className="flex gap-2">
            <Button data-testid="bulk-approve" onClick={bulkApprove} variant="outline" className="rounded-xl border-slate-300"><CheckCheck className="h-4 w-4 mr-1.5" /> Bulk approve ({sel.length})</Button>
            <Button data-testid="export-csv" onClick={exportCsv} variant="outline" className="rounded-xl border-slate-300"><Download className="h-4 w-4 mr-1.5" /> Export</Button>
          </div>
        </div>

        {loading ? <div className="flex justify-center py-20"><Loader2 className="h-7 w-7 animate-spin text-[#0E9AC8]" /></div> : (
          <div className="card-premium rounded-2xl overflow-hidden">
            {vendors.length === 0 ? <div className="p-16 text-center text-slate-500"><Users className="h-10 w-10 text-slate-300 mx-auto mb-3" />No vendors in this list.</div> : (
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-slate-500 text-xs uppercase"><tr>
                  <th className="p-3 w-10"></th><th className="text-left p-3">Vendor</th><th className="text-left p-3 hidden md:table-cell">Categories</th>
                  <th className="text-left p-3">Completion</th><th className="text-left p-3">Status</th><th className="p-3"></th></tr></thead>
                <tbody className="divide-y divide-slate-100">
                  {vendors.map((v) => (
                    <tr key={v.id} data-testid={`vrow-${v.id}`} className="hover:bg-slate-50">
                      <td className="p-3"><input type="checkbox" data-testid={`vcheck-${v.id}`} checked={sel.includes(v.id)} onChange={(e) => setSel(e.target.checked ? [...sel, v.id] : sel.filter((x) => x !== v.id))} /></td>
                      <td className="p-3"><p className="font-semibold text-slate-800">{v.company_name || v.name}</p><p className="text-xs text-slate-400">{v.email} · {v.mobile}</p></td>
                      <td className="p-3 hidden md:table-cell text-slate-500 text-xs">{(v.categories || []).slice(0, 2).join(", ") || "—"}</td>
                      <td className="p-3"><div className="flex items-center gap-2"><div className="h-1.5 w-16 bg-slate-100 rounded-full overflow-hidden"><div className="h-full bg-[#0E9AC8]" style={{ width: `${v.completion}%` }} /></div><span className="text-xs text-slate-500">{v.completion}%</span></div></td>
                      <td className="p-3"><span className={`text-xs font-semibold px-2 py-0.5 rounded-full capitalize ${SS[v.status]}`}>{v.status}</span>{v.verified && <span className="ml-1.5 inline-flex items-center gap-0.5 text-[10px] font-bold text-emerald-600" title="Nirmantra Verified"><CheckCheck className="h-3 w-3" />Verified</span>}</td>
                      <td className="p-3 text-right"><Button size="sm" variant="outline" data-testid={`view-${v.id}`} onClick={() => openDetail(v.id)} className="rounded-lg">Review</Button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
      </main>

      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="rounded-2xl max-w-2xl max-h-[85vh] overflow-y-auto">
          {detail && (<>
            <DialogHeader><DialogTitle className="font-display text-2xl">{detail.profile?.company_name || detail.name}</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div className="flex items-center gap-3 flex-wrap">
                <span className={`text-xs font-semibold px-2.5 py-1 rounded-full capitalize ${SS[detail.status]}`}>{detail.status}</span>
                {detail.verified && <span className="inline-flex items-center gap-1 text-xs font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2.5 py-1"><CheckCheck className="h-3.5 w-3.5" /> Nirmantra Verified</span>}
                <span className="text-sm text-slate-500">Completion {detail.completion}%</span>
              </div>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <Info k="Contact" v={`${detail.name} · ${detail.mobile}`} /><Info k="Email" v={detail.email} />
                <Info k="Experience" v={detail.profile?.experience_years ? `${detail.profile.experience_years} yrs` : "—"} /><Info k="Team" v={detail.profile?.team_size || "—"} />
                <Info k="Cities" v={detail.profile?.service_cities || "—"} /><Info k="Address" v={detail.profile?.business_address || "—"} />
              </div>
              <div><p className="text-xs uppercase text-slate-400 mb-1">Categories</p><p className="text-sm text-slate-700">{(detail.categories || []).join(", ") || "—"}</p></div>
              <div><p className="text-xs uppercase text-slate-400 mb-1">Documents</p><div className="flex flex-wrap gap-2">{Object.entries(detail.profile?.documents || {}).filter(([, val]) => val).map(([k, val]) => <span key={k} className="text-xs bg-slate-100 rounded px-2 py-1"><FileText className="inline h-3 w-3 mr-1" />{k.toUpperCase()}: {val}</span>) || "—"}{Object.values(detail.profile?.documents || {}).filter(Boolean).length === 0 && <span className="text-sm text-slate-400">None provided</span>}</div></div>

              <div className="border-t pt-3">
                <p className="text-xs uppercase text-slate-400 mb-2">Change status</p>
                <div className="flex flex-wrap gap-2">
                  {ACTIONS.map(([s, l, c]) => (
                    <button key={s} data-testid={`status-${s}`} onClick={() => setStatus(detail.id, s)} className={`${c} text-white text-xs font-semibold rounded-lg px-3 py-1.5 hover:opacity-90`}>{l}</button>
                  ))}
                </div>
                <div className="flex flex-wrap items-center gap-2 mt-3">
                  <button data-testid="verify-toggle" onClick={() => toggleVerify(detail.id, !detail.verified)} className={`text-xs font-semibold rounded-lg px-3 py-1.5 border ${detail.verified ? "bg-emerald-50 text-emerald-700 border-emerald-300" : "bg-white text-slate-600 border-slate-300 hover:border-emerald-400"}`}>
                    <CheckCheck className="inline h-3.5 w-3.5 mr-1" />{detail.verified ? "Verified Partner ✓ (click to unverify)" : "Mark as Nirmantra Verified"}
                  </button>
                  <button data-testid="remove-vendor" onClick={() => removeVendor(detail.id)} className="text-xs font-semibold rounded-lg px-3 py-1.5 bg-red-50 text-red-600 border border-red-200 hover:bg-red-100">Remove from network</button>
                </div>
                <p className="text-xs text-slate-400 mt-2">A vendor appears publicly only when <b>Live</b> and <b>Verified</b>.</p>
              </div>

              <div className="border-t pt-3">
                <p className="text-xs uppercase text-slate-400 mb-2">Notes & history</p>
                <div className="flex gap-2 mb-3"><Textarea data-testid="note-input" className="rounded-xl min-h-[40px]" placeholder="Add a note…" value={note} onChange={(e) => setNote(e.target.value)} /><Button data-testid="add-note" onClick={addNote} className="bg-[#0E9AC8] hover:bg-[#0B7EA4] text-white rounded-xl">Add</Button></div>
                <div className="space-y-2 max-h-40 overflow-y-auto">
                  {(detail.notes || []).map((n) => (
                    <div key={n.id} className="text-sm bg-slate-50 rounded-lg p-2.5"><p className="text-slate-700">{n.note}</p><p className="text-xs text-slate-400 mt-0.5">{n.author} · {new Date(n.created_at).toLocaleString()}</p></div>
                  ))}
                  {(!detail.notes || detail.notes.length === 0) && <p className="text-sm text-slate-400">No notes yet.</p>}
                </div>
              </div>
            </div>
          </>)}
        </DialogContent>
      </Dialog>
    </div>
  );
}
const Info = ({ k, v }) => <div><p className="text-xs uppercase text-slate-400">{k}</p><p className="text-slate-700">{v}</p></div>;
